import { SEASONS, SEASON_LABELS, type Season } from '@lib/enums/season.enum'
import { cn } from '@/lib/utils'

/**
 * Which season a piece of work should be done in: four buttons, the year's
 * order, with the season it is now marked so "later" is a choice somebody can
 * see they made (docs/seasonal-work.md).
 *
 * A row of buttons rather than a select, like the job levels: one tap with a
 * gloved thumb, and the whole year visible at once. There is no "none" button.
 * Work recorded in the survey always has a season; null only reaches here on
 * work recorded before seasons existed, and then nothing is pressed.
 */
export function SeasonPicker({
  value,
  current,
  label,
  onChange,
  controlClassName,
}: {
  value: Season | null
  /** The season it is now, by this device's date. */
  current: Season
  /** What the buttons choose a season for, read out to a screen reader. */
  label: string
  onChange: (season: Season) => void
  controlClassName?: string | undefined
}) {
  return (
    <div role="group" aria-label={label} className="grid grid-cols-4 gap-1">
      {SEASONS.map((season) => {
        const active = value === season
        return (
          <button
            key={season}
            type="button"
            aria-pressed={active}
            onClick={() => {
              onChange(season)
            }}
            className={cn(
              'flex min-w-0 flex-col items-center justify-center rounded-md border px-1 text-sm leading-tight transition-colors',
              controlClassName,
              active
                ? 'border-brand bg-brand-subtle font-medium text-primary-text'
                : 'border-default bg-surface text-secondary-text hover:bg-subtle',
            )}
          >
            <span className="truncate">{SEASON_LABELS[season]}</span>
            {season === current ? (
              <span className="text-[10px] text-muted-text">now</span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}
